import $ from 'jquery';
import GoogleMapsLoader from 'google-maps';
import GoogleMapsConfig from './GoogleMapsConfig';
import DistrictData from './DistrictsData';

GoogleMapsLoader.LIBRARIES = ['geometry'];
GoogleMapsLoader.KEY = GoogleMapsConfig.key;
GoogleMapsLoader.VERSION = GoogleMapsConfig.version;

const defaults = {
  name: 'district-locator', // Used for id of map and to plot relative location icon within the /img directory.
  zoom: 15, // Max zoom of the map when initialized.
  defaultLocation: { lat: 38.5689826, lng: -121.480051 }, // Digital Deployment HQ
  icons: {
    user: '/sites/default/themes/dtheme/img/marker-user.svg', // Filename of the user marker icon
    district: '/sites/default/themes/dtheme/img/marker-district.svg', // Filename of the node marker icon
  },
};

export default class LocatorMap {
  constructor(element, options) {
    this.element = element;
    this.$element = $(element);
    this.settings = Object.assign({}, defaults, options);

    GoogleMapsLoader.load((google) => {
      this.google = google;
      this.geocoder = new google.maps.Geocoder();
      this.bounds = new google.maps.LatLngBounds();
      this.districts = this.setDistricts(DistrictData);
      this.map = this.createMap();
      this.pins = [];
      this.configureUserLocation().finally(() => {
        this.configurePins();
        this.setBoundries();
        this.addPins();
        this.ready();
      });
    });
  }

  configureUserLocation() {
    return new Promise((resolve, reject) => {
      // Quit if a valid address hasn't been added.
      if (!this.userLocation.length) {
        resolve(window.console.warn('Please input a valid city, address, or zipcode.'));
        return;
      }

      // Fetch location using Google Maps API.
      const promise = this.geocode(this.userLocation);
      promise.then((data) => {
        resolve(this.plotUserLocation(data.location));
      });
      promise.catch(() => {
        reject(new Error('No user location found. Continuing on mapping the other points.'));
      });
    });
  }

  plotUserLocation({ lat, lng }) {
    const pos = new this.google.maps.LatLng(lat(), lng());
    const info = {
      content: `<div class="map-info">
        <header>
          <h2 class="title">Your location</h2>
        </header>
      </div>`,
    };
    const settings = {
      map: this.map,
      position: pos,
      title: 'You are here',
      animation: this.google.maps.Animation.DROP,
      infoWindow: new this.google.maps.InfoWindow(info),
      icon: this.settings.icons.user,
    };

    // Plot district user is within.
    this.plotDistrict(pos);

    // Stage pins in an array.
    this.pins.push(settings);

    // Extend the map bounds to include this location.
    this.bounds.extend(pos);
  }

  plotDistrict(position) {
    let repFound = false;
    this.districts.forEach((district) => {
      if (this.google.maps.geometry.poly.containsLocation(position, district.polygon)) {
        district.polygon.setMap(this.map);
        // We want to first see if a rep is present.
        const $rep = $('.division-reps .node').filter(`[data-division="${district.title}"]`);
        if ($rep.length) {
          // We also want to show the rep associated to this district.
          $rep.addClass('active');
          // Switch repFound flag
          repFound = true;
        }
      }
    });

    // Set default rep message if a rep was not found within the queried location.
    if (!repFound) {
      const noRep = document.createElement('div');
      const message = document.createElement('p');
      noRep.className = 'node no-rep active';
      message.textContent = 'The provided address is not within our District Boundaries. Please call 760-347-3484 for assistance. Thank you.';
      noRep.appendChild(message);
      $('.division-reps .reps').append(noRep);
    }

    // Display the rep container.
    $('.division-reps').addClass('show');
  }

  createMap() {
    return new this.google.maps.Map(document.getElementById(`${this.settings.name}-map`), {
      zoom: this.settings.zoom,
      center: this.settings.defaultLocation,
    });
  }

  setBoundries() {
    // Adjust map bounds by the points to display.
    this.map.fitBounds(this.bounds);
  }

  configurePins() {
    return this.$nodes.each((i, node) => {
      const $address = $(node).find('.address');
      const lat = $address.attr('data-lat');
      const lng = $address.attr('data-lng');
      const pos = new this.google.maps.LatLng(Number(lat), Number(lng));
      const info = {
        content: `<div class="map-info">${$(node).html()}</div>`,
      };

      const settings = {
        map: this.map,
        position: pos,
        title: $(node).find('.title').text(),
        animation: this.google.maps.Animation.DROP,
        infoWindow: new this.google.maps.InfoWindow(info),
        icon: this.settings.icons.district,
      };

      // Stage pins in an array.
      this.pins.push(settings);

      // Extend the map bounds to include this location.
      this.bounds.extend(pos);
    });
  }

  addPins() {
    const delay = this.pins.length > 10 ? 25 : 125;
    this.pins.forEach((settings, i) => {
      setTimeout(() => {
        this.dropPin(settings);
      }, i * delay);
    });
  }

  dropPin(settings) {
    const pin = new this.google.maps.Marker(settings);
    pin.addListener('click', () => {
      this.pins.forEach(p => p.infoWindow.close());
      this.districts.forEach(d => d.infoWindow.close());
      settings.infoWindow.open(this.map, pin);
    });
  }

  setDistricts(data) {
    const districts = [];

    // Return empty array of districts if they aren't set to show on this display.
    if (!this.element.classList.contains('show-districts')) {
      return districts;
    }

    // Iterate over all districts and add a polygon attribute.
    data.districts.forEach((district) => {
      district.polygon = new this.google.maps.Polygon({
        paths: district.area,
        strokeColor: district.color,
        strokeOpacity: 0.75,
        strokeWeight: 2,
        fillColor: district.color,
        fillOpacity: 0.25,
      });

      district.infoWindow = new this.google.maps.InfoWindow({
        content: `<div class="district-title">${district.title}</div>`,
      });

      this.google.maps.event.addListener(district.polygon, 'click', (event) => {
        district.infoWindow.setPosition(event.latLng);
        district.infoWindow.open(this.map);
      });

      // Add the district to our array.
      districts.push(district);
    });

    return districts;
  }

  geocode(location) {
    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ address: location }, (data, status) => {
        if (status === this.google.maps.GeocoderStatus.OK) {
          resolve(data[0].geometry);
        } else {
          reject(new Error(status));
        }
      });
    });
  }

  ready() {
    this.$element.addClass('ready');
  }

  get $nodes() {
    return this.$element.find('.node-district-locator--result');
  }

  get userLocation() {
    return this.automaticLocation ? this.automaticLocation : this.manualLocation;
  }

  get automaticLocation() {
    return this.$element.find('#edit-location').val();
  }

  get manualLocation() {
    return this.$element.find('#edit-search').val();
  }
}
